import { useEffect, useState } from 'react';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { type registration } from '@/api';
import { infoFormResponseTableQueryKey, loadInfoFormResponseForTable, type InfoFormResponseTableRow } from '@/api/registration';
import { type ApiResponse } from '@/api/tenantClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type OnChangeValue } from 'chakra-react-select';
import BaseSimpleSelectElement, { type Option as SelectElementOption } from '@/base/Form/SimpleSelectElement';
import {
  UiHStack,
  UiSimpleGrid,
  UiStack,
  uiStyles,
} from '@/lib/ui';
import Datatable from './Datatable';
import BaseLoadingSpinner from '@/base/Loading/Spinner';
import { type MRT_ColumnFiltersState, type MRT_PaginationState, type MRT_SortingState, type MRT_VisibilityState } from 'mantine-react-table';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useEventOptionsQuery } from '@/registration/hook/useEventOptions';
import { useAttendeeCategoryQuery } from '@/registration/hook/useAttendeeCategoryQuery';
import { useFormMetadataListQuery } from '@/registration/hook/useFormMetadataListQuery';

interface Params {
  filters: any
  sorting: any
  pagination: any
  search: string
  eventId?: string
  attendeeCategoryId?: string
  formId?: string
}

const initialVisibilityState: MRT_VisibilityState = {
  'ticket.function': false,
  'ticket.tour': false,
  'ticket.workshop': false,
  createdAtDate: false,
  createdAtTime: false,
  completedAtDate: false,
  completedAtTime: false
};

const useGetInfoFormResponse = ({ filters, sorting, pagination, search, eventId, attendeeCategoryId, formId, }: Params & { setPagination: React.Dispatch<React.SetStateAction<any>> }) => {
  const { createTenantAdminApiRequest, isLoading: isTenantLoading } = useTenantApi();
  return useQuery<ApiResponse<InfoFormResponseTableRow>, Error>({
    queryKey: [infoFormResponseTableQueryKey, pagination, filters, sorting, search, eventId, attendeeCategoryId, formId],
    queryFn: async (): Promise<ApiResponse<InfoFormResponseTableRow>> => {
      return await loadInfoFormResponseForTable(createTenantAdminApiRequest)({
        eventId,
        attendeeCategoryId,
        formId,
        page: pagination.pageIndex,
        size: pagination.pageSize,
        filters,
        sorting,
        search
      });
    },
    keepPreviousData: true,
    staleTime: 30_000,
    enabled: !isTenantLoading,
  });
};

const LOCAL_TABLE_STORAGE_KEY = 'info_form_response_table';

enum GroupBy {
  AttendeeGroup = 'attendeeGroup',
  Form = 'form',
}

const groupByOptions = [
  { label: 'By attendee group', value: GroupBy.AttendeeGroup },
  { label: 'By form', value: GroupBy.Form },
];

const FormResponseTable = () => {
  const queryClient = useQueryClient();
  const { tenantCode } = useRegisterRoute();
  const [filters, setFilters] = useLocalStorage<MRT_ColumnFiltersState>(`${LOCAL_TABLE_STORAGE_KEY}_filters_${tenantCode}`, []);
  const [sorting, setSorting] = useLocalStorage<MRT_SortingState>(`${LOCAL_TABLE_STORAGE_KEY}_sorting_${tenantCode}`, []);
  const [search, setSearch] = useLocalStorage<string>(`${LOCAL_TABLE_STORAGE_KEY}_search_${tenantCode}`, '');
  const [pagination, setPagination] = useLocalStorage<MRT_PaginationState>(`${LOCAL_TABLE_STORAGE_KEY}_pagination_${tenantCode}`, { pageIndex: 0, pageSize: 10 });
  const [columnVisibility, setColumnVisibility] = useLocalStorage<MRT_VisibilityState>(`${LOCAL_TABLE_STORAGE_KEY}_columns_${tenantCode}`, initialVisibilityState);
  const [eventId, setEventId] = useState<string>('');
  const [attendeeCategoryList, setAttendeeCategoryList] = useState<any>([]);
  const [formOptions, setFormOptions] = useState<SelectElementOption[]>([]);
  const [attendeeCategoryId, setAttendeeCategoryId] = useState<string>('');
  const [groupBy, setGroupBy] = useState<string>('');
  const [formId, setFormId] = useState<string>('');
  const { data, isLoading, refetch, isError, isFetching } = useGetInfoFormResponse({
    filters,
    sorting,
    pagination,
    search,
    setPagination,
    eventId,
    attendeeCategoryId,
    formId,
  });
  const { eventOptions, isLoading: isEventOptionsLoading } = useEventOptionsQuery();
  const { data: attendeeCategoryListData } = useAttendeeCategoryQuery(eventId);
  const { data: formMetadataList } = useFormMetadataListQuery({ eventId: Number(eventId) });

  // Fetch the attendee group options.
  useEffect(() => {
    if (attendeeCategoryListData && Array.isArray(attendeeCategoryListData)) {
      const attendeeGroupOptions = attendeeCategoryListData.map((item: registration.AttendeeCategoryData) => {
        return { value: item.id.toString(), label: item.name };
      });
      setAttendeeCategoryList(attendeeGroupOptions);
    } else {
      setAttendeeCategoryList([]);
    }
    setAttendeeCategoryId('');
  }, [
    attendeeCategoryListData,
    setAttendeeCategoryList,
  ]);

  // Fetch the form options.
  useEffect(() => {
    if (formMetadataList?.items && Array.isArray(formMetadataList.items)) {
      const tempformOptions = [];
      for (const formMetDada of formMetadataList.items) {
        tempformOptions.push({
          value: formMetDada.id,
          label: formMetDada.name || `FORM UNNAMED (ID: ${formMetDada.id})`,
        });
      }
      setFormOptions(tempformOptions);
    } else {
      setFormOptions([]);
    }
    setFormId('');
  }, [
    formMetadataList,
    setFormOptions,
  ]);

  // // !!! This is NOT the way to call the backend API. We NEED to use React Query...
  // useEffect(() => {
  //   if (eventId) {
  //     const fetchAttendeeCategoryList = async () => {
  //       const result = await registration.loadAttendeeCategoryList(createTenantAdminApiRequest)({ eventId });
  //       setAttendeeCategoryList(result.items[0].map((item: registration.AttendeeCategoryData) => { return { value: item.id.toString(), label: item.name }; }));
  //       setAttendeeCategoryId('');
  //     };
  //
  //     void fetchAttendeeCategoryList();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [eventId]);

  // useEffect(() => {
  //   if (attendeeCategoryId) {
  //     const fetchData = async () => {
  //       await refetch();
  //     };
  //     void fetchData();
  //   }
  // }, [attendeeCategoryId, refetch]);

  useEffect(() => {
    if (groupBy !== GroupBy.AttendeeGroup) {
      setAttendeeCategoryId('');
    }
    if (groupBy !== GroupBy.Form) {
      setFormId('');
    }
  }, [groupBy]);

  const handleChangeEvent = async (option: OnChangeValue<SelectElementOption, false>) => {
    const eventId = option?.value ? `${option?.value}` : '';
    setEventId(eventId);
  };
  const handleChangeGroupBy = async (option: OnChangeValue<SelectElementOption, false>) => {
    setGroupBy(option?.value ? `${option?.value}` : '');
  };
  const handleChangeAttendeeCategory = (option: OnChangeValue<SelectElementOption, false>) => {
    setAttendeeCategoryId(option?.value ? `${option?.value}` : '');
  };
  const handleChangeFormId = async (option: OnChangeValue<SelectElementOption, false>) => {
    const tempFormId = option?.value ? `${option?.value}` : '';
    console.log('tempFormId', tempFormId);
    setFormId(tempFormId);
    console.log('formId', formId);
  };

  console.log('formId', formId);

  return (
    <>
      <UiHStack
        borderWidth={'1px'}
        borderStyle={'solid'}
        borderRadius={uiStyles.borderRadius}
        borderColor={'gray.300'}
        p={8}
        justifyContent={'flex-start'}
        spacing={8}
      >
        <UiSimpleGrid columns={3} spacing={8} flexGrow={1}>
          <BaseSimpleSelectElement
            optionValue={eventId}
            onChange={handleChangeEvent}
            isLoading={isEventOptionsLoading}
            options={eventOptions}
            placeholder={'Select event'}
          />
          <BaseSimpleSelectElement
            optionValue={groupBy}
            onChange={handleChangeGroupBy}
            isLoading={isEventOptionsLoading}
            options={groupByOptions}
            placeholder={'Export by'}
          />
          {groupBy === GroupBy.AttendeeGroup && (
            <BaseSimpleSelectElement
              optionValue={attendeeCategoryId}
              onChange={handleChangeAttendeeCategory}
              options={attendeeCategoryList}
              placeholder={'Select attendee group'}
            />
          )}
          {groupBy === GroupBy.Form && (
            <BaseSimpleSelectElement
              optionValue={formId}
              onChange={handleChangeFormId}
              options={formOptions}
              placeholder={'Select form'}
            />
          )}
        </UiSimpleGrid>
        {/* <BaseButtonIconText */}
        {/*  Icon={UiIconMagnifyingGlass} */}
        {/*  onClick={onSearch} */}
        {/* > */}
        {/*  Search */}
        {/* </BaseButtonIconText> */}
      </UiHStack>
      <UiStack height={8} />
      {/* <BaseDividerHorizontal height={8} /> */}
      {(!data && isLoading && (!!attendeeCategoryId || !!formId)) && (
        <BaseLoadingSpinner />
      )}
      {eventId && (attendeeCategoryId || formId) && data?.items && Array.isArray(data?.items) && (
        <Datatable
          data={data.items.map((FormResponse) => {
            return {
              ...FormResponse,
              createdAt: new Date(FormResponse.createdAt),
              confirmDelegateTypeAt: FormResponse.confirmDelegateTypeAt === null ? null : new Date(FormResponse.confirmDelegateTypeAt)
            };
          })}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          refetch={refetch}
          filters={filters}
          setFilters={setFilters}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          pagination={pagination}
          setPagination={setPagination}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
          rowCount={data?.pagination?.total ?? 0}
          personalColumns={data?.infoFormQuestions?.personal}
          additionalColumns={data?.infoFormQuestions?.additional}
          eventId={eventId}
          attendeeCategoryId={attendeeCategoryId}
          formId={formId}
          statusSelectOptions={data?.selectOptions?.statusSelectOptions ?? []}
          delegateTypeSelectOptions={data?.selectOptions?.delegateTypeSelectOptions ?? []}
          tourTicketsSelectOptions={data?.selectOptions?.tourTicketsSelectOptions ?? []}
          workshopTicketsSelectOptions={data?.selectOptions?.workshopTicketsSelectOptions ?? []}
          functionTicketsSelectOptions={data?.selectOptions?.functionTicketsSelectOptions ?? []}
        />
      )}
    </>
  );
};

export default FormResponseTable;
